import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useBagShippingQuote, type BagShippingService } from '../hooks/useBagShippingQuote';
import { useGenerateBagLabel, type GenerateBagLabelResult } from '../hooks/useGenerateBagLabel';

type Props = {
  bagId: string | null;
  open: boolean;
  onClose: () => void;
};

type ConfirmState = { service: BagShippingService } | null;

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatRange = (range: BagShippingService['delivery_range']): string => {
  if (!range) return 'prazo não informado';
  if (range.min === range.max) return `${range.min} dia${range.min === 1 ? '' : 's'}`;
  return `${range.min}–${range.max} dias`;
};

export const BagShippingDialog = ({ bagId, open, onClose }: Props) => {
  const { toast } = useToast();
  const quote = useBagShippingQuote(bagId ?? '');
  const generate = useGenerateBagLabel(bagId ?? '');

  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [result, setResult] = useState<GenerateBagLabelResult | null>(null);
  const [services, setServices] = useState<BagShippingService[]>([]);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !bagId) {
      setConfirm(null);
      setResult(null);
      setServices([]);
      setQuoteError(null);
      setGenerateError(null);
      return;
    }
    setQuoteError(null);
    quote.mutateAsync()
      .then((s) => setServices(s))
      .catch((err: Error) => setQuoteError(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bagId]);

  const handleConfirmGenerate = async () => {
    if (!confirm) return;
    setGenerateError(null);
    try {
      const res = await generate.mutateAsync(confirm.service.id);
      setResult(res);
      setConfirm(null);
      toast({ title: 'Etiqueta gerada com sucesso!' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar etiqueta';
      setGenerateError(message);
    }
  };

  const handleClose = () => {
    setConfirm(null);
    setResult(null);
    setServices([]);
    setQuoteError(null);
    setGenerateError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cotação de frete</DialogTitle>
          <DialogDescription>
            Selecione um serviço para gerar a etiqueta agregada da sacolinha.
          </DialogDescription>
        </DialogHeader>

        {result && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <h3 className="text-lg font-semibold">Etiqueta gerada com sucesso</h3>
            {result.tracking_code && (
              <p className="text-sm">
                Código de rastreio: <span className="font-mono font-medium">{result.tracking_code}</span>
              </p>
            )}
            {result.label_url && (
              <a
                href={result.label_url}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary underline"
              >
                Abrir etiqueta (PDF)
              </a>
            )}
            <DialogFooter className="mt-4 w-full">
              <Button onClick={handleClose} variant="default">Fechar</Button>
            </DialogFooter>
          </div>
        )}

        {!result && confirm && (
          <div className="flex flex-col gap-4 py-4">
            <p className="text-sm">
              Confirmar geração de etiqueta com <strong>{confirm.service.company} – {confirm.service.name}</strong>
              {' '}por <strong>{formatCurrency(confirm.service.price)}</strong>?
            </p>
            <p className="text-xs text-muted-foreground">
              Esta ação é irreversível: a sacolinha vai para "Etiqueta gerada" e não pode mais ser reaberta.
            </p>
            {generateError && (
              <Alert variant="destructive">
                <AlertDescription>{generateError}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setConfirm(null); setGenerateError(null); }} disabled={generate.isPending}>
                Voltar
              </Button>
              <Button onClick={handleConfirmGenerate} disabled={generate.isPending}>
                {generate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar e gerar etiqueta
              </Button>
            </DialogFooter>
          </div>
        )}

        {!result && !confirm && (
          <div className="flex flex-col gap-3 py-2">
            {quote.isPending && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {quoteError && (
              <Alert variant="destructive">
                <AlertDescription>{quoteError}</AlertDescription>
              </Alert>
            )}

            {!quote.isPending && !quoteError && services.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nenhum serviço de frete disponível para esta sacolinha.
              </p>
            )}

            {!quote.isPending && services.length > 0 && (
              <ul className="flex flex-col gap-2">
                {services.map((s) => (
                  <li
                    key={`${s.company}-${s.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border p-3"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{s.company} – {s.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {formatRange(s.delivery_range)} • {formatCurrency(s.price)}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setConfirm({ service: s })}
                    >
                      Gerar etiqueta com este serviço
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <DialogFooter className="mt-2">
              <Button variant="ghost" onClick={handleClose}>Fechar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
